// GlobalShipmentEditFormModal.tsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import {
  getGlobalShipments,
  updateGeneratedGlobalShipment,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { validSignedURL } from "../../../utils/validateSignedUrl";

interface GlobalShipmentEditFormModalProps {
  visible: boolean;
  onClose: () => void;
  shipment: any;
}

// ─── Validation helpers ───────────────────────────────────────────────────────
const isValidDate = (val: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(val) && !isNaN(Date.parse(val));

const toNumber = (val: any) => parseFloat(String(val));

const formatDateForInput = (iso: string) => {
  if (!iso) return "";
  return iso.split("T")[0];
};

// ─── Component ────────────────────────────────────────────────────────────────
const GlobalShipmentEditFormModal: React.FC<
  GlobalShipmentEditFormModalProps
> = ({ visible, onClose, shipment }) => {
  const dispatch = useDispatch<any>();
  const { loadingAction, pageSize, page, sort, status, search } = useSelector(
    (state: any) => state.chinaTracker.shipments.global,
  );

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      containerNo: "",
      sealNo: "",
      vesselVoyage: "",
      ETD: "",
      shippingLine: "",
      freightLine: "",
      ETA: "",
      location: "",
      items: [] as any[],
    },
  });

  useEffect(() => {
    if (!shipment) return;
    reset({
      containerNo: shipment.containerNo ?? "",
      sealNo: shipment.sealNo ?? "",
      vesselVoyage: shipment.vesselVoyage ?? "",
      ETD: formatDateForInput(shipment.ETD),
      shippingLine: shipment.shippingLine ?? "",
      freightLine: shipment.freightLine ?? "",
      ETA: formatDateForInput(shipment.ETA),
      location: shipment.location ?? "",
      items: (shipment.items ?? []).map((item: any) => ({
        _id: item._id ?? "",
        factoryOrderDetail:
          item.factoryOrderDetail?._id ?? item.factoryOrderDetail ?? "",
        itemCode: item.itemCode ?? "",
        orderNumber: item.orderNumber ?? "",
        itemDescription: item.itemDescription ?? "",
        material: item.material ?? "",
        spec: item.spec ?? "",
        label: item.label ?? "",
        qtyPerCarton: item.qtyPerCarton ?? "",
        qtyCartons: item.qtyCartons ?? "",
        qtyTotal: item.qtyTotal ?? "",
        gwPerCarton: item.gwPerCarton ?? "",
        gwCartons: item.gwCartons ?? "",
        gwTotal: item.gwTotal ?? "",
        nwPerCarton: item.nwPerCarton ?? "",
        nwCartons: item.nwCartons ?? "",
        nwTotal: item.nwTotal ?? "",
        notes: item.notes ?? "",
      })),
    });
  }, [shipment, reset]);

  const { fields } = useFieldArray({ control, name: "items" });

  const onSubmit = async (data: any) => {
    const payload = {
      containerNo: data.containerNo.trim(),
      sealNo: data.sealNo.trim(),
      vesselVoyage: data.vesselVoyage?.trim(),
      ETD: data.ETD,
      ETA: data.ETA,
      shippingLine: data.shippingLine?.trim(),
      freightLine: data.freightLine?.trim(),
      location: data.location?.trim(),
      items: data.items.map((item: any) => ({
        _id: item._id,
        itemCode: item.itemCode.trim(),
        orderNumber: item.orderNumber.toString().trim(),
        itemDescription: item.itemDescription?.trim(),
        material: item.material?.trim(),
        spec: item.spec?.trim(),
        label: item.label?.trim(),
        qtyPerCarton: toNumber(item.qtyPerCarton),
        qtyCartons: toNumber(item.qtyCartons),
        qtyTotal: toNumber(item.qtyTotal),
        gwPerCarton: toNumber(item.gwPerCarton),
        gwCartons: toNumber(item.gwCartons),
        gwTotal: toNumber(item.gwTotal),
        nwPerCarton: toNumber(item.nwPerCarton),
        nwCartons: toNumber(item.nwCartons),
        nwTotal: toNumber(item.nwTotal),
        notes: item.notes?.trim(),
      })),
    };

    const result = await dispatch(
      updateGeneratedGlobalShipment({ _id: shipment._id, payload }),
    );
    if (!result.error) {
      onClose();
      dispatch(
        getGlobalShipments({
          page,
          pageSize,
          sort: sort.direction === "asc" ? sort.key : "-" + sort.key,
          status,
          search,
        }),
      ).unwrap();
    }
  };

  // ── Images ───────────────────────────────────────────────────────────────
  const [images, setImages] = useState<Record<string, string>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!shipment?.items?.length) return;
    const uniqueItemCodes = [
      ...new Set(shipment.items.map((item: any) => item.itemCode)),
    ] as string[];

    uniqueItemCodes.forEach((itemCode: string) => {
      if (!itemCode || images[itemCode]) return;
      setLoadingMap((prev) => ({ ...prev, [itemCode]: true }));
      validSignedURL(
        itemCode,
        "label",
        () => {},
        (url: string) => {
          setImages((prev) => ({ ...prev, [itemCode]: url }));
          setLoadingMap((prev) => ({ ...prev, [itemCode]: false }));
        },
      );
    });
  }, [shipment]);

  // ── Header field definitions ──────────────────────────────────────────────
  const headerFields: {
    name: any;
    label: string;
    isDate?: boolean;
    editable: boolean;
  }[] = [
    { name: "containerNo", label: "Container No", editable: true },
    { name: "sealNo", label: "Seal No.", editable: true },
    { name: "vesselVoyage", label: "Vessel / Voyage", editable: true },
    { name: "ETD", label: "ETD (YYYY-MM-DD)", isDate: true, editable: true },
    { name: "shippingLine", label: "Shipping Line", editable: true },
    { name: "freightLine", label: "Freight Line", editable: true },
    { name: "ETA", label: "ETA (YYYY-MM-DD)", isDate: true, editable: true },
    { name: "location", label: "Location", editable: true },
  ];

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.title}>Edit Global Shipment</Text>

            {/* ── Header Grid ── */}
            <View style={styles.headerGrid}>
              {headerFields.map(({ name, label, isDate, editable }) => (
                <View key={name} style={styles.headerCell}>
                  <Text style={styles.cellLabel}>
                    {label}
                    <Text style={styles.asterisk}> *</Text>
                  </Text>
                  <View style={{ flex: 1 }}>
                    <Controller
                      control={control}
                      name={name}
                      rules={{
                        required: `${label} is required`,
                        ...(isDate && {
                          validate: (v) =>
                            isValidDate(v) ||
                            `${label} must be a valid date (YYYY-MM-DD)`,
                        }),
                        ...(name === "ETA" && {
                          validate: (v) => {
                            if (!v) return "ETA is required";
                            if (!isValidDate(v))
                              return "ETA must be a valid date (YYYY-MM-DD)";
                            const etd = getValues("ETD");
                            if (etd && new Date(v) < new Date(etd))
                              return "ETA cannot be earlier than ETD";
                            return true;
                          },
                        }),
                      }}
                      render={({ field: { onChange, value } }) => (
                        <TextInput
                          style={[
                            styles.cellInput,
                            !!errors[name as keyof typeof errors] &&
                              styles.inputError,
                          ]}
                          editable={editable}
                          value={value}
                          onChangeText={onChange}
                          placeholderTextColor="#bbb"
                          placeholder={isDate ? "YYYY-MM-DD" : ""}
                        />
                      )}
                    />
                    {errors[name as keyof typeof errors] && (
                      <Text style={styles.errorText}>
                        {(errors[name as keyof typeof errors] as any)?.message}
                      </Text>
                    )}
                  </View>
                </View>
              ))}
            </View>

            {/* ── Item Cards ── */}
            {fields.map((field, index) => {
              const staticItemCode = shipment?.items?.[index]?.itemCode;
              return (
                <ItemCard
                  key={field.id}
                  index={index}
                  control={control}
                  field={field}
                  errors={errors}
                  loadingMap={loadingMap}
                  images={images}
                  staticItemCode={staticItemCode}
                />
              );
            })}

            {fields.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>No shipment items found.</Text>
              </View>
            )}
          </ScrollView>

          {/* ── Footer ── */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              disabled={isSubmitting || loadingAction}
            >
              <Text style={styles.closeText}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.submitButton,
                (isSubmitting || loadingAction) && styles.submitDisabled,
              ]}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting || loadingAction}
            >
              {isSubmitting || loadingAction ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.submitText}>Save Changes</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

// ─── Item Card ────────────────────────────────────────────────────────────────
const ItemCard = ({
  index,
  control,
  field,
  errors,
  loadingMap,
  images,
  staticItemCode,
}: {
  index: number;
  control: any;
  field: any;
  errors: any;
  loadingMap: any;
  images: any;
  staticItemCode: any;
}) => {
  const itemError = (fieldName: string) =>
    errors?.items?.[index]?.[fieldName]?.message;

  const numericRules = (label: string) => ({
    required: `${label} is required`,
    validate: (v: any) => {
      const n = parseFloat(String(v));
      if (isNaN(n)) return `${label} must be a number`;
      if (n < 0) return `${label} cannot be negative`;
      return true;
    },
  });

  const cartonParityRules = (label: string, referenceField: string) => ({
    ...numericRules(label),
    validate: (v: any, formValues: any) => {
      const n = parseFloat(String(v));
      if (isNaN(n)) return `${label} must be a number`;
      if (n < 0) return `${label} cannot be negative`;
      const ref = parseFloat(
        String(formValues?.items?.[index]?.[referenceField] ?? ""),
      );
      if (!isNaN(ref) && n !== ref)
        return `${label} must equal qtyCartons (${ref})`;
      return true;
    },
  });

  const CalcRow = ({
    rowLabel,
    perCartonName,
    cartonsName,
    totalName,
    unitLabel,
    cartonsRules,
  }: {
    rowLabel: string;
    perCartonName: string;
    cartonsName: string;
    totalName: string;
    unitLabel: string;
    cartonsRules?: object;
  }) => (
    <View style={styles.calcRow}>
      <Text style={styles.calcRowLabel}>
        {rowLabel} <Text style={styles.asterisk}>*</Text>
      </Text>
      <View style={styles.calcInputs}>
        <Controller
          control={control}
          name={`items.${index}.${perCartonName}`}
          rules={numericRules(`${rowLabel} per carton`)}
          render={({ field: { onChange, value } }) => (
            <TextInput
              style={[
                styles.calcInput,
                !!itemError(perCartonName) && styles.inputError,
              ]}
              value={String(value)}
              onChangeText={onChange}
              keyboardType="numeric"
            />
          )}
        />
        <Text style={styles.calcUnit}>
          {unitLabel === "pcs" ? "pcs/carton" : "kg/carton"}
        </Text>
        <Text style={styles.calcOp}>×</Text>

        <Controller
          control={control}
          name={`items.${index}.${cartonsName}`}
          rules={cartonsRules ?? numericRules(`${rowLabel} cartons`)}
          render={({ field: { onChange, value } }) => (
            <TextInput
              style={[
                styles.calcInput,
                !!itemError(cartonsName) && styles.inputError,
              ]}
              value={String(value)}
              onChangeText={onChange}
              keyboardType="numeric"
            />
          )}
        />
        <Text style={styles.calcUnit}>cartons</Text>
        <Text style={styles.calcOp}>=</Text>

        <Controller
          control={control}
          name={`items.${index}.${totalName}`}
          rules={numericRules(`${rowLabel} total`)}
          render={({ field: { onChange, value } }) => (
            <TextInput
              style={[
                styles.calcInput,
                !!itemError(totalName) && styles.inputError,
              ]}
              value={String(value)}
              onChangeText={onChange}
              keyboardType="numeric"
            />
          )}
        />
        <Text style={styles.calcUnit}>{unitLabel}</Text>
      </View>
      {(itemError(perCartonName) ||
        itemError(cartonsName) ||
        itemError(totalName)) && (
        <Text style={styles.errorText}>
          {itemError(perCartonName) ||
            itemError(cartonsName) ||
            itemError(totalName)}
        </Text>
      )}
    </View>
  );

  return (
    <View style={styles.itemCard}>
      <View style={styles.itemCardHeader}>
        <Text style={styles.itemCardTitle} numberOfLines={1}>
          {`${field.itemDescription || "—"} | ${staticItemCode || "—"}`}
        </Text>
      </View>

      <View style={styles.itemBody}>
        {/* Item Code | Order Number */}
        <View style={styles.itemRow}>
          <View style={styles.itemHalf}>
            <Text style={styles.itemLabel}>
              Item Code <Text style={styles.asterisk}>*</Text>
            </Text>
            <Controller
              control={control}
              name={`items.${index}.itemCode`}
              rules={{ required: "Item Code is required" }}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  style={[
                    styles.itemInput,
                    !!itemError("itemCode") && styles.inputError,
                  ]}
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
            {itemError("itemCode") && (
              <Text style={styles.errorText}>{itemError("itemCode")}</Text>
            )}
          </View>
          <View style={styles.itemHalf}>
            <Text style={styles.itemLabel}>
              Order Number <Text style={styles.asterisk}>*</Text>
            </Text>
            <Controller
              control={control}
              name={`items.${index}.orderNumber`}
              rules={{ required: "Order Number is required" }}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  style={[
                    styles.itemInput,
                    !!itemError("orderNumber") && styles.inputError,
                  ]}
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
            {itemError("orderNumber") && (
              <Text style={styles.errorText}>{itemError("orderNumber")}</Text>
            )}
          </View>
        </View>

        {/* Item Description */}
        <View style={styles.itemFullRow}>
          <Text style={styles.itemLabel}>
            Item Description <Text style={styles.asterisk}>*</Text>
          </Text>
          <Controller
            control={control}
            name={`items.${index}.itemDescription`}
            rules={{ required: "Item Description is required" }}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[
                  styles.itemInput,
                  !!itemError("itemDescription") && styles.inputError,
                ]}
                value={value}
                onChangeText={onChange}
              />
            )}
          />
          {itemError("itemDescription") && (
            <Text style={styles.errorText}>{itemError("itemDescription")}</Text>
          )}
        </View>

        {/* Material | Spec | Label */}
        <View style={styles.itemRow}>
          {(
            [
              { key: "material", label: "Material" },
              { key: "spec", label: "Spec" },
              { key: "label", label: "Label" },
            ] as const
          ).map(({ key, label }) => (
            <View key={key} style={styles.itemThird}>
              <Text style={styles.itemLabel}>
                {label} <Text style={styles.asterisk}>*</Text>
              </Text>
              <Controller
                control={control}
                name={`items.${index}.${key}`}
                rules={{ required: `${label} is required` }}
                render={({ field: { onChange, value } }) => (
                  <TextInput
                    style={[
                      styles.itemInput,
                      !!itemError(key) && styles.inputError,
                    ]}
                    value={value}
                    onChangeText={onChange}
                  />
                )}
              />
              {itemError(key) && (
                <Text style={styles.errorText}>{itemError(key)}</Text>
              )}
            </View>
          ))}
        </View>

        {/* Calc rows + image */}
        <View style={styles.calcSection}>
          <View style={styles.calcRows}>
            <CalcRow
              rowLabel="Quantity"
              perCartonName="qtyPerCarton"
              cartonsName="qtyCartons"
              totalName="qtyTotal"
              unitLabel="pcs"
            />
            <CalcRow
              rowLabel="Gross Weight"
              perCartonName="gwPerCarton"
              cartonsName="gwCartons"
              totalName="gwTotal"
              unitLabel="kg"
              cartonsRules={cartonParityRules("gwCartons", "qtyCartons")}
            />
            <CalcRow
              rowLabel="Net Weight"
              perCartonName="nwPerCarton"
              cartonsName="nwCartons"
              totalName="nwTotal"
              unitLabel="kg"
              cartonsRules={cartonParityRules("nwCartons", "qtyCartons")}
            />
          </View>

          <View style={styles.imageContainer}>
            {loadingMap[staticItemCode] && (
              <ActivityIndicator size="large" style={styles.loader} />
            )}
            {images[staticItemCode] && (
              <Image
                source={{ uri: images[staticItemCode] }}
                style={styles.productImage}
                resizeMode="contain"
              />
            )}
          </View>
        </View>

        {/* Notes */}
        <View style={styles.itemFullRow}>
          <Text style={styles.itemLabel}>Notes</Text>
          <Controller
            control={control}
            name={`items.${index}.notes`}
            render={({ field: { onChange, value } }) => (
              <TextInput
                style={[styles.itemInput, styles.notesInput]}
                value={value}
                onChangeText={onChange}
                multiline
              />
            )}
          />
        </View>
      </View>
    </View>
  );
};

export default GlobalShipmentEditFormModal;

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.25)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    width: "95%",
    maxHeight: "92%",
    backgroundColor: "#f2f2f2",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111",
    marginBottom: 18,
    letterSpacing: 0.1,
  },
  headerGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 16,
    rowGap: 12,
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#e2e2e2",
  },
  headerCell: {
    width: "47%",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  cellLabel: {
    fontSize: 12,
    color: "#333",
    fontWeight: "500",
    minWidth: 90,
    paddingTop: 8,
  },
  cellInput: {
    flex: 1,
    height: 34,
    borderWidth: 1,
    borderColor: "#d4d4d4",
    borderRadius: 6,
    backgroundColor: "#f9f9f9",
    paddingHorizontal: 10,
    fontSize: 13,
    color: "#111",
  },
  asterisk: { color: "#e03131", fontWeight: "700" },
  inputError: { borderColor: "#e03131", backgroundColor: "#fff5f5" },
  errorText: { fontSize: 11, color: "#e03131", marginTop: 2 },
  itemCard: {
    backgroundColor: "#fff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#e2e2e2",
    marginBottom: 14,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  itemCardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 11,
    backgroundColor: "#f8f8f8",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e2e2",
  },
  itemCardTitle: { fontSize: 13, fontWeight: "600", color: "#222", flex: 1 },
  itemBody: { padding: 14, gap: 12 },
  itemRow: { flexDirection: "row", gap: 12 },
  itemFullRow: { gap: 4 },
  itemHalf: { flex: 1 },
  itemThird: { flex: 1 },
  itemLabel: {
    fontSize: 12,
    color: "#444",
    marginBottom: 4,
    fontWeight: "500",
  },
  itemInput: {
    height: 34,
    borderWidth: 1,
    borderColor: "#d4d4d4",
    borderRadius: 6,
    backgroundColor: "#f9f9f9",
    paddingHorizontal: 10,
    fontSize: 13,
    color: "#111",
  },
  notesInput: { height: 72, textAlignVertical: "top", paddingTop: 8 },
  calcSection: { flexDirection: "row", gap: 12, alignItems: "flex-start" },
  calcRows: { flex: 1, gap: 10 },
  calcRow: { gap: 4 },
  calcRowLabel: {
    fontSize: 12,
    color: "#444",
    fontWeight: "500",
    marginBottom: 3,
  },
  calcInputs: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
  },
  calcInput: {
    width: 54,
    height: 32,
    borderWidth: 1,
    borderColor: "#d4d4d4",
    borderRadius: 5,
    backgroundColor: "#f9f9f9",
    paddingHorizontal: 6,
    fontSize: 12,
    color: "#111",
    textAlign: "center",
  },
  calcUnit: { fontSize: 11, color: "#555" },
  calcOp: {
    fontSize: 13,
    fontWeight: "600",
    color: "#222",
    paddingHorizontal: 1,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#e2e2e2",
  },
  closeButton: {
    borderWidth: 1,
    borderColor: "#bbb",
    borderRadius: 6,
    paddingHorizontal: 22,
    paddingVertical: 9,
    backgroundColor: "#fff",
  },
  closeText: { fontSize: 14, color: "#333", fontWeight: "500" },
  submitButton: {
    backgroundColor: "#4dabf7",
    borderRadius: 6,
    paddingHorizontal: 32,
    paddingVertical: 10,
    minWidth: 90,
    alignItems: "center",
  },
  submitDisabled: { backgroundColor: "#a5d8ff" },
  submitText: { color: "#fff", fontWeight: "700", fontSize: 14 },
  emptyState: { padding: 24, alignItems: "center" },
  emptyText: { color: "#999", fontSize: 13 },
  imageContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    minHeight: 250,
  },
  productImage: { width: "100%", height: "100%", flex: 1, minHeight: 250 },
  loader: { position: "absolute", zIndex: 1 },
});
